// A real, finished work produced with Espresso — the flagship showcase specimen.
// Volume PDFs live in /public/showcase and are served statically (deploy-safe).

export const SHOWCASE = {
  slug: "human-flourishing",
  title: "The Book of Human Flourishing",
  subtitle: "An open, evidence-based research and education engine dedicated to improving human life.",
  mission: "Make humanity's most useful knowledge accessible to every human being.",
  ref: "SHOWCASE · 001",
  publisher: "Espresso by Mahakali Studios",
  cover: "https://static.prod-images.emergentagent.com/jobs/2dbac991-9431-4c1c-baca-4d7548e28de1/images/0a8e20d6acf8d28a3789d0a5c83e381292f3947f94ab91556ca7a22672041cfd.jpeg",
  og: "https://static.prod-images.emergentagent.com/jobs/2dbac991-9431-4c1c-baca-4d7548e28de1/images/8fb49c37b7d8e18f1f67047975a89809f36289d8d5f81e22b1170438b03cabe6.jpeg",
  intro:
    "Human flourishing is the sustained development of a life that is vital, meaningful, capable, connected, and ethically grounded. This body of knowledge brings philosophical, scientific, social and contemplative perspectives into conversation — then structures them into a living compendium that is rigorous enough for serious inquiry and clear enough to guide everyday practice.",
  stats: [
    { value: "4,661", label: "Pages" },
    { value: "5", label: "Volumes" },
    { value: "17", label: "Knowledge sections" },
    { value: "24", label: "Fields per entry" },
  ],
  // How this specimen was produced inside Espresso.
  pipeline: [
    "Master Index (CSV)",
    "Research Questions",
    "Evidence & Sources",
    "Human Review",
    "Chapter Synthesis",
    "Cross-Linking",
    "Typeset Volumes",
  ],
  pullQuote:
    "Flourishing is more than feeling good. A person may endure grief, difficulty and sacrifice while participating in a life of profound love, integrity and purpose.",
  volumes: [
    {
      n: "I",
      title: "General",
      desc: "The main body of knowledge — the full library of chapters across every section of human flourishing.",
      pages: "4,221 pages",
      file: "/showcase/human-flourishing-vol1-general.pdf",
    },
    {
      n: "II",
      title: "The Library of Human Liberation",
      desc: "A focused collection on freedom, dignity and the conditions that release human potential.",
      pages: "59 pages",
      file: "/showcase/human-flourishing-vol2-library-of-human-liberation.pdf",
    },
    {
      n: "III",
      title: "A System That Teaches Humanity How to Solve Its Own Problems",
      desc: "The applied problem-solving framework — from clear perception to practical, ethical experimentation.",
      pages: "102 pages",
      file: "/showcase/human-flourishing-vol3-system-to-solve-problems.pdf",
    },
    {
      n: "IV",
      title: "The Ultimate Measure of Espresso",
      desc: "The impact thesis — what it means to measure knowledge by how much it helps real human lives.",
      pages: "26 pages",
      file: "/showcase/human-flourishing-vol4-ultimate-measure.pdf",
    },
    {
      n: "V",
      title: "Master Knowledge Index",
      desc: "The foundational index — every section, module and research question that drives the engine.",
      pages: "253 pages",
      file: "/showcase/human-flourishing-vol5-master-index.pdf",
    },
  ],
  sections: [
    { n: "01", title: "Foundations of Human Flourishing", blurb: "The sustained development of a life that is vital, meaningful, capable, connected and ethically grounded." },
    { n: "02", title: "Knowledge and Truth", blurb: "Every decision depends on two questions: what do we know, and how do we know it is true?" },
    { n: "03", title: "Health and Wellbeing", blurb: "Not merely the absence of disease, but the conditions through which people develop, recover and belong." },
    { n: "04", title: "Justice and Human Rights", blurb: "The moral languages for what each person is owed and how power should be exercised." },
    { n: "05", title: "Education and Learning", blurb: "Shaping not only what people know, but how they attend, judge, imagine, cooperate and create." },
    { n: "06", title: "Livelihood and Wealth", blurb: "How human beings meet material needs and create enough stability to live with dignity." },
    { n: "07", title: "Science and Discovery", blurb: "A disciplined practice of learning how the world resists our assumptions." },
    { n: "08", title: "Peace and Cooperation", blurb: "Living human capacities: to regulate fear, negotiate difference, and repair harm." },
    { n: "09", title: "Planetary Stewardship", blurb: "Caring for Earth's living systems and shared future with humility, competence and justice." },
    { n: "10", title: "Technology for the Public Good", blurb: "A way of arranging attention, power, knowledge, labour and possibility." },
    { n: "11", title: "Culture and Human Experience", blurb: "A primary condition through which people perceive, interpret, organise and evaluate experience." },
    { n: "12", title: "Institutions and Governance", blurb: "The arrangements that make security, learning, health, voice and shared meaning possible over time." },
    { n: "13", title: "Applied Problem Solving", blurb: "Moving from an uncertain situation to a workable one through judgment and experimentation." },
    { n: "14", title: "Human Futures", blurb: "Futures are not discovered in advance — they are imagined, contested, built and revised." },
    { n: "15", title: "Research and Publishing Engine", blurb: "The system that makes the whole work a living intellectual resource open to revision." },
    { n: "16", title: "Editorial Constitution", blurb: "The promise protecting scientific integrity and human dignity across every entry." },
    { n: "17", title: "Development Roadmap", blurb: "A deliberate roadmap joining intellectual depth with practical usefulness." },
  ],
};
